import React from 'react';

/**
 * ShiftNotes brand lockup.
 *
 * Variants:
 *  - `full`   : SN monogram + SHIFTNOTES wordmark + "DEVELOP. EVALUATE. ELEVATE." tagline
 *  - `stack`  : SN monogram + stacked wordmark, tagline optional
 *  - `mark`   : SN monogram only
 *  - `inline` : compact monogram + wordmark, no tagline (nav/sidebar use)
 *
 * Colors default to the deep-royal brand blue on light backgrounds.
 * Pass `tone="light"` to render for dark backgrounds.
 */
export function BrandLogo({
  variant = 'full',
  tone = 'dark',
  showTagline = true,
  className = '',
}) {
  const mark = tone === 'light' ? '#ffffff' : '#1e40af';
  const wordmark = tone === 'light' ? '#ffffff' : '#0b1f4d';
  const tag = tone === 'light' ? 'rgba(255,255,255,0.75)' : '#1e40af';
  const TAGLINE = 'Accelerate player development.';

  const Monogram = ({ size = 44 }) => (
    <svg
      viewBox="0 0 96 72"
      width={size * 1.35}
      height={size}
      aria-hidden
      style={{ overflow: 'visible' }}
    >
      <text
        x="48"
        y="58"
        textAnchor="middle"
        fill={mark}
        style={{
          fontFamily: "'Anton', 'Barlow Condensed', 'Impact', sans-serif",
          fontSize: '72px',
          fontWeight: 400,
          letterSpacing: '-2px',
        }}
        transform="skewX(-14)"
      >
        SN
      </text>
    </svg>
  );

  if (variant === 'mark') {
    return (
      <div className={className}>
        <Monogram size={48} />
      </div>
    );
  }

  if (variant === 'inline') {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <Monogram size={34} />
        <div className="leading-none">
          <div
            className="font-brand tracking-wide"
            style={{ color: wordmark, fontSize: '20px', letterSpacing: '0.03em' }}
          >
            SHIFTNOTES
            <sup className="ml-0.5" style={{ color: mark, fontSize: '9px' }}>&trade;</sup>
          </div>
          <div
            className="uppercase tracking-[0.22em] mt-0.5"
            style={{ color: tag, fontSize: '8px' }}
          >
            {TAGLINE}
          </div>
        </div>
      </div>
    );
  }

  if (variant === 'stack') {
    return (
      <div className={`flex flex-col items-center text-center ${className}`}>
        <Monogram size={60} />
        <div
          className="font-brand mt-3"
          style={{ color: wordmark, fontSize: '32px', letterSpacing: '0.03em' }}
        >
          SHIFTNOTES
          <sup className="ml-1" style={{ color: mark, fontSize: '12px' }}>&trade;</sup>
        </div>
        {showTagline && (
          <div
            className="uppercase mt-1 tracking-[0.28em]"
            style={{ color: tag, fontSize: '11px' }}
          >
            {TAGLINE}
          </div>
        )}
      </div>
    );
  }

  // full - horizontal SN + wordmark + tagline (matches the reference exactly)
  return (
    <div className={`flex items-center gap-3 ${className}`}>
      <Monogram size={54} />
      <div className="leading-none">
        <div
          className="font-brand"
          style={{ color: wordmark, fontSize: '34px', letterSpacing: '0.03em' }}
        >
          SHIFTNOTES
          <sup className="ml-1" style={{ color: mark, fontSize: '13px' }}>&trade;</sup>
        </div>
        {showTagline && (
          <div
            className="uppercase mt-1.5 tracking-[0.28em]"
            style={{ color: tag, fontSize: '10px' }}
          >
            {TAGLINE}
          </div>
        )}
      </div>
    </div>
  );
}

export default BrandLogo;
